/**
 * Variant Shipping Filter
 *
 * Two-layer filtering system for product variant visibility:
 *
 * Layer 1 — Global state validation
 *   User state (from localStorage) is checked against the metaobject list
 *   (window.shippingAllowedStates). If the state is not allowed, all variant
 *   pickers are blocked and an unavailability notice is shown.
 *
 * Layer 2 — Delivery vs Shipping (rate-based)
 *   Cached shipping rates determine the logistics mode:
 *   - If any rate name contains "Delivery" → delivery mode → all variants visible
 *   - Otherwise → shipping mode → only variants with data-shipping-allowed="true" are shown
 *
 *   This defaults to shipping mode when no cached rates exist, then re-evaluates
 *   when the shippingRatesReady event fires.
 */

class VariantShippingFilter {
  static NOTICE_CLASS = 'variant-state-unavailable-notice';

  constructor() {
    this.isEnabled = window.allowedStatesEnabled === true;
    if (!this.isEnabled) return;

    this.allowedStates = window.shippingAllowedStates || [];
    this.injectNoticeStyles();
    this.apply();
    this.setupEventListeners();
  }

  // ── Style management ──────────────────────────────────────────────────

  getStyleTag() {
    let style = document.getElementById('variant-shipping-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'variant-shipping-style';
      document.head.appendChild(style);
    }
    return style;
  }

  injectNoticeStyles() {
    if (document.getElementById('variant-state-notice-styles')) return;
    const style = document.createElement('style');
    style.id = 'variant-state-notice-styles';
    style.textContent = `
      .${VariantShippingFilter.NOTICE_CLASS} {
        border: 2px solid #C45A3A;
        background: #fff;
        color: #C45A3A;
        border-radius: 10px;
        padding: 14px 16px;
        font-size: 1em;
        font-weight: 600;
        line-height: 1.5;
        text-align: center;
        box-shadow: 0 6px 20px rgba(196, 90, 58, 0.22);
        margin-block: 0.5em;
      }
      .${VariantShippingFilter.NOTICE_CLASS} p { margin: 0; }
      .${VariantShippingFilter.NOTICE_CLASS} a {
        color: #C45A3A;
        font-weight: 700;
        text-decoration: underline;
      }
      .${VariantShippingFilter.NOTICE_CLASS} a:hover { color: #8B3A25; }
    `;
    document.head.appendChild(style);
  }

  // ── Core logic ────────────────────────────────────────────────────────

  apply() {
    const state = localStorage.getItem('user_state');
    const zip = localStorage.getItem('zip_code');

    // Layer 1: Global state validation
    if (state && this.allowedStates.length > 0 && !this.allowedStates.includes(state)) {
      this.blockAll();
      return;
    }

    // No zip yet (modal still pending) — clear filters, show everything
    if (!zip) {
      this.clearFilters();
      return;
    }

    // Layer 2: Delivery vs Shipping based on cached rates
    if (this.hasDeliveryRates(zip)) {
      this.setDeliveryMode();
    } else {
      this.setShippingMode();
    }
  }

  hasDeliveryRates(zip) {
    try {
      var cached = JSON.parse(localStorage.getItem('shipping_rates_v2_' + zip));
      if (cached && cached.shipping_rates && cached.shipping_rates.length > 0) {
        return cached.shipping_rates.some(function(r) {
          return r.name.indexOf('Delivery') !== -1;
        });
      }
    } catch (e) {}
    return false;
  }

  // ── Layer 1: Block all (state not allowed) ────────────────────────────

  blockAll() {
    var style = this.getStyleTag();
    style.textContent = '';
    document.querySelectorAll('variant-picker').forEach(function(picker) {
      this.setPickerUnavailable(picker);
    }.bind(this));
  }

  // ── Layer 2: Delivery mode (all variants visible) ─────────────────────

  setDeliveryMode() {
    var style = this.getStyleTag();
    style.textContent = '';
    document.querySelectorAll('variant-picker').forEach(function(picker) {
      this.setPickerAvailable(picker);
    }.bind(this));
  }

  // ── Layer 2: Shipping mode (filter by shipping_allowed) ───────────────

  setShippingMode() {
    var style = this.getStyleTag();
    style.textContent = [
      '.variant-option__button-label:has(input[data-shipping-allowed="false"]) { display: none !important; }',
      'option[data-shipping-allowed="false"] { display: none !important; }'
    ].join('\n');

    document.querySelectorAll('variant-picker').forEach(function(picker) {
      this.reSelectPicker(picker);
      this.checkPickerShippingAvailability(picker);
    }.bind(this));
  }

  // ── Clear all filters ─────────────────────────────────────────────────

  clearFilters() {
    var style = this.getStyleTag();
    style.textContent = '';
    document.querySelectorAll('variant-picker').forEach(function(picker) {
      this.setPickerAvailable(picker);
    }.bind(this));
  }

  // ── Re-selection: pick a visible variant when current is hidden ───────

  reSelectPicker(picker) {
    picker.querySelectorAll('.variant-option--buttons').forEach(function(fieldset) {
      var checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!checked || checked.dataset.shippingAllowed !== 'false') return;

      var inputs = fieldset.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].dataset.shippingAllowed !== 'false') {
          inputs[i].checked = true;
          inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    });

    picker.querySelectorAll('.variant-option__select').forEach(function(select) {
      var selected = select.options[select.selectedIndex];
      if (!selected || selected.dataset.shippingAllowed !== 'false') return;

      for (var i = 0; i < select.options.length; i++) {
        var option = select.options[i];
        if (!option.disabled && option.dataset.shippingAllowed !== 'false') {
          option.selected = true;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    });
  }

  // ── Availability check: are there ANY visible variants? ───────────────

  checkPickerShippingAvailability(picker) {
    var allInputs = picker.querySelectorAll(
      'input[type="radio"], .variant-option__select option'
    );
    var hasAvailable = Array.from(allInputs).some(function(el) {
      return el.dataset.shippingAllowed !== 'false';
    });

    if (hasAvailable) {
      this.setPickerAvailable(picker);
    } else {
      this.setPickerUnavailable(picker);
    }
  }

  // ── Picker state: unavailable ─────────────────────────────────────────

  setPickerUnavailable(picker) {
    var form = picker.querySelector('.variant-picker__form');
    if (form) form.style.display = 'none';

    var notice = picker.querySelector('.' + VariantShippingFilter.NOTICE_CLASS);
    if (!notice) {
      var message = window.allowedStatesNoticeMessage
        || 'This product is not available for shipping to your state. Please <a href="/pages/contact-us">contact us</a> for more information.';
      notice = document.createElement('div');
      notice.className = VariantShippingFilter.NOTICE_CLASS;
      notice.setAttribute('role', 'alert');
      notice.innerHTML = message;
      picker.prepend(notice);
    }
    notice.style.display = '';

    this.disableBuyButtons(picker);
  }

  // ── Picker state: available ───────────────────────────────────────────

  setPickerAvailable(picker) {
    var form = picker.querySelector('.variant-picker__form');
    if (form) form.style.display = '';

    var notice = picker.querySelector('.' + VariantShippingFilter.NOTICE_CLASS);
    if (notice) notice.style.display = 'none';

    this.enableBuyButtons(picker);
  }

  // ── Buy-button helpers ────────────────────────────────────────────────

  findBuyButtonContainers(picker) {
    var containers = [];
    var section = picker.closest('.shopify-section, .product-information');

    if (section) {
      section.querySelectorAll('product-form-component').forEach(function(pfc) {
        containers.push(pfc);
      });
    }

    var stickyBar = document.querySelector('[data-sticky-product-bar] product-form-component');
    if (stickyBar) containers.push(stickyBar);

    return containers;
  }

  disableBuyButtons(picker) {
    this.findBuyButtonContainers(picker).forEach(function(container) {
      var btn = container.querySelector('button[name="add"]');
      if (btn) {
        btn.disabled = true;
        btn.dataset.stateUnavailable = 'true';
      }

      var accel = container.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (accel) {
        accel.setAttribute('hidden', '');
        accel.dataset.stateUnavailable = 'true';
      }
    });
  }

  enableBuyButtons(picker) {
    this.findBuyButtonContainers(picker).forEach(function(container) {
      var btn = container.querySelector('button[name="add"][data-state-unavailable]');
      if (btn) {
        btn.disabled = false;
        delete btn.dataset.stateUnavailable;
      }

      var accel = container.querySelector('[data-state-unavailable][ref="acceleratedCheckoutButtonContainer"]');
      if (accel) {
        accel.removeAttribute('hidden');
        delete accel.dataset.stateUnavailable;
      }
    });
  }

  // ── Event listeners & observers ───────────────────────────────────────

  setupEventListeners() {
    window.addEventListener('zipcodeUpdated', this.apply.bind(this));
    window.addEventListener('shippingRatesReady', this.apply.bind(this));
    this.observeVariantPickers();
  }

  observeVariantPickers() {
    var self = this;
    var timeoutId = null;

    new MutationObserver(function(mutations) {
      var hasNewPickers = false;

      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.tagName === 'VARIANT-PICKER' || (node.querySelector && node.querySelector('variant-picker'))) {
            hasNewPickers = true;
          }
        });
      });

      if (hasNewPickers) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(function() { self.apply(); }, 100);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() { new VariantShippingFilter(); });
} else {
  new VariantShippingFilter();
}
